import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { useSSE } from "../hooks/useSSE";
import LeadsTable from "../components/LeadsTable";
import LeadDetail from "../components/LeadDetail";
import EmailEditor from "../components/EmailEditor";

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState({ status: "", priority: "", lead_status: "" });
  const [loading, setLoading] = useState(true);

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.set("status", filter.status);
      if (filter.priority) params.set("priority", filter.priority);
      if (filter.lead_status) params.set("lead_status", filter.lead_status);
      params.set("limit", "50");
      const data = await api(`/leads?${params}`);
      setLeads(data.leads);
      setTotal(data.total);
    } catch (err) {
      console.error("Failed to fetch leads:", err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Live updates
  useSSE("lead:new", (lead) => {
    setLeads((prev) => [lead, ...prev]);
    setTotal((t) => t + 1);
  });

  useSSE("lead:updated", (updated) => {
    setLeads((prev) =>
      prev.map((l) => (l.id === updated.id ? updated : l))
    );
    if (selectedId === updated.id) {
      setSelectedLead(updated);
    }
  });

  const [selectedLead, setSelectedLead] = useState(null);

  const handleSelect = async (id) => {
    setSelectedId(id);
    try {
      const lead = await api(`/leads/${id}`);
      setSelectedLead(lead);
    } catch (err) {
      console.error("Failed to fetch lead:", err);
    }
  };

  const handleClose = () => {
    setSelectedId(null);
    setSelectedLead(null);
  };

  const handleLeadUpdated = (updated) => {
    setLeads((prev) =>
      prev.map((l) => (l.id === updated.id ? updated : l))
    );
    setSelectedLead(updated);
  };

  return (
    <div className="leads-page">
      <div className="leads-header">
        <h2>Leads ({total})</h2>
        <div className="filters">
          <select
            value={filter.status}
            onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={filter.priority}
            onChange={(e) => setFilter((f) => ({ ...f, priority: e.target.value }))}
          >
            <option value="">All priorities</option>
            <option value="hot">Hot</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={filter.lead_status}
            onChange={(e) => setFilter((f) => ({ ...f, lead_status: e.target.value }))}
          >
            <option value="">All pipeline</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="replied">Replied</option>
            <option value="meeting_booked">Meeting Booked</option>
            <option value="sent_pricing">Sent Pricing</option>
            <option value="sent_contract">Sent Contract</option>
            <option value="not_interested">Not Interested</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="loading">Loading leads...</p>
      ) : leads.length === 0 ? (
        <p className="empty">No leads found</p>
      ) : (
        <div className={`leads-layout ${selectedLead ? "with-panel" : ""}`}>
          <LeadsTable leads={leads} selectedId={selectedId} onSelect={handleSelect} />

          {selectedLead && (
            <div className="lead-panel">
              <button className="close-btn" onClick={handleClose}>&times;</button>
              <LeadDetail lead={selectedLead} onUpdate={handleLeadUpdated} />
              <EmailEditor lead={selectedLead} onUpdate={handleLeadUpdated} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
